import { createCachedSelector } from 're-reselect';
import { createSelector } from 'reselect';
import { AbridgedAsset } from 'store/assets/types';
import { OrganisationListItem } from 'store/organisations/types';
import {
  SearchFilterOption,
  SearchFilters,
  SearchOption,
  SearchOptions,
  SearchRequest,
  SearchResults,
  SearchState,
} from 'store/search/types';
import { State } from 'store/types';

type SearchStateTree = Omit<Partial<State>, 'search'> & Pick<State, 'search'>;

const selectSearch = (state: SearchStateTree): SearchState => state.search;

const selectSearchOptions = createSelector(
  selectSearch,
  (search: SearchState): SearchOptions => search.options,
);

export const selectSearchOpened = createSelector(
  selectSearch,
  (search: SearchState): boolean => search.opened,
);

export const selectSearchOrganisationFilterOptions = createSelector(
  selectSearchOptions,
  (options: SearchOptions): SearchFilterOption[] =>
    options.filter.organisations?.items || [],
);

export const selectSearchOptionsSort = createSelector(
  selectSearchOptions,
  (options: SearchOptions): SearchOption[] => options.sort,
);

export const selectSearchRequest = createSelector(
  selectSearch,
  (search: SearchState): SearchRequest => search.request,
);

const selectSearchFilter = createSelector(
  selectSearchRequest,
  (request: SearchRequest): SearchFilters => request.filter,
);

export const selectSearchBlockedByOrganisationFilter = createSelector(
  selectSearchOrganisationFilterOptions,
  selectSearchFilter,
  (options, filter): boolean =>
    options.length > 25 && filter.organisations.length === 0,
);

export const selectSearchOrganisationFilterDisplay = createSelector(
  selectSearchRequest,
  selectSearchOrganisationFilterOptions,
  (request: SearchRequest, organisations): string =>
    request.filter.organisations.length === 0
      ? ''
      : request.filter.organisations.length > 1
        ? `${request.filter.organisations.length} Organisations`
        : organisations.find(
            (organisation) =>
              organisation.id === request.filter.organisations[0],
          )?.name || 'Unknown',
);

export const selectSearchIsFiltered = createSelector(
  selectSearchFilter,
  (filter: SearchFilters): boolean =>
    !!filter.assessment_statuses.length ||
    !!filter.organisations.length ||
    !!filter.search,
);

export const selectSearchSortValue = createSelector(
  selectSearchRequest,
  (request: SearchRequest): string => request.sort,
);

export const selectSearchTextFilter = createSelector(
  selectSearchFilter,
  (filter: SearchFilters): string => filter.search,
);

const selectSearchResults = createSelector(
  selectSearch,
  (search: SearchState): SearchResults => search.results,
);

export const selectSearchTotal = createSelector(
  selectSearchResults,
  (results: SearchResults): number | null => results.total,
);

const selectSearchAssets = createSelector(
  selectSearchResults,
  (results: SearchResults): Record<string, AbridgedAsset> => results.assets,
);

export const selectSearchAsset = createCachedSelector(
  selectSearchAssets,
  (_: SearchStateTree, id: string) => id,
  (assets: Record<string, AbridgedAsset>, id): AbridgedAsset => assets[id]!,
)((_: SearchStateTree, id: string) => id);

export const selectSearchAssetIds = createSelector(
  selectSearchResults,
  (results: SearchResults): string[] => results.asset_ids,
);

export const selectChosenSearchAssets = createSelector(
  selectSearch,
  (search: SearchState): string[] => search.chosen,
);

export const selectChosenSearchAssetsCount = createSelector(
  selectChosenSearchAssets,
  (chosen: string[]): number => chosen.length,
);

export const selectChosenSearchAssetsAreChosen = createSelector(
  selectChosenSearchAssetsCount,
  (count: number): boolean => !!count,
);

const selectSearchOrganisations = createSelector(
  selectSearchResults,
  (results: SearchResults): Record<string, OrganisationListItem> =>
    results.organisations,
);

export const selectSearchOrganisation = createCachedSelector(
  selectSearchOrganisations,
  (_: SearchStateTree, id: string) => id,
  (organisations, id) => organisations[id]!,
)((_: SearchStateTree, id: string) => id);

export const selectIsSearchAssetChosen = createCachedSelector(
  selectChosenSearchAssets,
  (_: SearchStateTree, id: string) => id,
  (chosen: string[], id: string): boolean => chosen.includes(id),
)((_: SearchStateTree, id: string) => id);
